import { camelCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, Path } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useLanguageValues } from '../../../../data/data/select-values';
import {
  getInputValidation,
  getRegistrationFieldErrors,
} from '../../../../data/GraphQL/api/graphql-errors';
import {
  Gender,
  InsuranceType,
  Language,
  MfaAuthenticationType,
  RegistrationPersonItemDataFragment,
  RegistrationPersonUpdateInput,
} from '../../../../data/types/api/graphql';
import { getAge, maxBirthDate } from '../../../../data/utils/date.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { InputTextAreaController } from '../../../../elements/form/controllers/input-textarea';
import { FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { Answer } from '../../form/person-schema';
import {
  RegistrationPersonFormValues,
  toUpdateInput,
  useRegistrationPersonForm,
} from '../../form/use-registration-person-form';

/**
 * The server's code for a refused field ("invalid_bsn"), camel-cased, as the
 * message id shown on it.
 */
const fieldMessageIds: { [code: string]: string } = {
  required: 'common.errors.field-required',
  tooLong: 'common.errors.max-length',
  invalidEmail: 'common.errors.invalid-mail-address',
  invalidPhone: 'common.errors.invalid-phone',
  invalidBirthDate: 'common.errors.invalid-date',
  invalidBsn: 'registrations.edit.errors.invalid-bsn',
  invalidChoice: 'common.errors.generic',
  consentRequired: 'registrations.edit.errors.minor-consent-required',
};

const required = (id: string) => (
  <>
    <CustomFormattedMessage id={id} />
    <small>*</small>
  </>
);

const yesNoOptions = (notApplicable = false) => [
  { key: 'yes' as Answer, title: <CustomFormattedMessage id="common.yes" /> },
  { key: 'no' as Answer, title: <CustomFormattedMessage id="common.no" /> },
  ...(notApplicable
    ? [
        {
          key: '' as Answer,
          title: <CustomFormattedMessage id="registrations.not-applicable" />,
        },
      ]
    : []),
];

type RegistrationPersonModalProps = {
  person: RegistrationPersonItemDataFragment;
  onConfirm: (input: RegistrationPersonUpdateInput) => Promise<void>;
  onClose?: () => void;
};

/**
 * Correct everything the household submitted for one person before
 * accepting, plus the patient settings the person gets: the same choices as
 * on the patient form. The server checks the registration form's rules again;
 * every field it refuses is shown on that field, so the practice can fix them
 * all without leaving the modal.
 */
export function RegistrationPersonModal({
  person,
  onConfirm,
  onClose,
}: RegistrationPersonModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const { form } = useRegistrationPersonForm(person);

  const { data: languageData } = useLanguageValues();

  const [submitting, setSubmitting] = useState<boolean>(false);

  const onHandleConfirm = useCallback(() => {
    form.handleSubmit(async values => {
      setSubmitting(true);

      try {
        await onConfirm(toUpdateInput(person.id, values));
      } catch (error) {
        // InputField translates the message itself, so pass the id.
        const fields = getRegistrationFieldErrors(error);
        const validation = getInputValidation(error)?.validation;

        const refused = fields !== undefined || validation === 'AlreadyExists';

        if (fields) {
          fields.forEach(option => {
            form.setError(option.field as Path<RegistrationPersonFormValues>, {
              message:
                fieldMessageIds[camelCase(option.code)] ??
                'common.errors.generic',
            });
          });
        } else if (validation === 'AlreadyExists') {
          form.setError('email', { message: 'registrations.email.in-use' });
        }

        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: intl.formatMessage({
            id: refused ? 'registrations.edit.invalid' : 'common.error',
          }),
        });
      } finally {
        setSubmitting(false);
      }
    })();
  }, [form, intl, onConfirm, person.id, showAlertMessage]);

  const mobileApp = form.watch('mobileApp') === true;
  const birthDate = form.watch('birthDate');
  const previousGpInNetherlands = form.watch('previousGpInNetherlands');
  const recordTransferConsent = form.watch('recordTransferConsent');
  const insuranceType = form.watch('insuranceType');

  // Only asked for a minor, as on the registration form.
  const isMinor = useMemo(() => {
    const age = getAge(birthDate);

    return age !== undefined && age < 18;
  }, [birthDate]);

  return (
    <Modal
      testId="registration-person-edit"
      width={ModalWidth.LARGE}
      title={<CustomFormattedMessage id="registrations.edit.title" />}
      onClose={onClose}
      buttons={
        <>
          <Button
            testId="registration-person-edit-cancel"
            type={ButtonType.SECONDARY}
            inverted
            onClick={onClose}
          >
            <CustomFormattedMessage id="common.cancel" />
          </Button>

          <Button
            testId="registration-person-edit-confirm"
            icon={submitting ? ButtonIcon.LOADING : ButtonIcon.NONE}
            disabled={submitting}
            onClick={onHandleConfirm}
          >
            <CustomFormattedMessage id="common.save" />
          </Button>
        </>
      }
    >
      <FormProvider {...form}>
        <h3>
          <CustomFormattedMessage id="registrations.edit.section.person" />
        </h3>

        <FormRow>
          <InputFieldController
            name="initials"
            label={required('common.initials')}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="firstName"
            label={required('common.firstname')}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="lastName"
            label={required('common.lastname')}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="birthName"
            label={
              <CustomFormattedMessage id="registrations.edit.birth-name" />
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            icon={BaseInputIcon.DATE}
            type="date"
            name="birthDate"
            label={required('common.birthdate')}
            max={maxBirthDate}
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<Gender>
            type={InputCheckboxType.RADIO}
            name="gender"
            label={<CustomFormattedMessage id="common.gender" />}
            options={[
              {
                key: Gender.Male,
                title: <CustomFormattedMessage id="common.gender.male" />,
              },
              {
                key: Gender.Female,
                title: <CustomFormattedMessage id="common.gender.female" />,
              },
              {
                key: Gender.Other,
                title: <CustomFormattedMessage id="common.gender.other" />,
              },
            ]}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="bsn"
            label={<CustomFormattedMessage id="registrations.bsn" />}
          />
        </FormRow>

        {isMinor && (
          <FormRow>
            <InputCheckboxController<Answer>
              type={InputCheckboxType.RADIO}
              name="minorConsent"
              label={required('registrations.minor-consent')}
              options={yesNoOptions()}
            />
          </FormRow>
        )}

        <h3>
          <CustomFormattedMessage id="registrations.edit.section.contact" />
        </h3>

        <FormRow>
          <InputFieldController
            name="email"
            label={
              <>
                <CustomFormattedMessage id="common.email" />
                {mobileApp && <small>*</small>}
              </>
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            type="phone"
            name="phoneNumber"
            label={required('common.phone')}
          />
        </FormRow>

        <h3>
          <CustomFormattedMessage id="registrations.own-address" />
        </h3>
        <p>
          <CustomFormattedMessage id="registrations.edit.own-address.hint" />
        </p>

        <FormRow>
          <InputFieldController
            name="street"
            label={<CustomFormattedMessage id="registrations.edit.street" />}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="houseNumber"
            label={
              <CustomFormattedMessage id="registrations.edit.house-number" />
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="houseNumberAddition"
            label={
              <CustomFormattedMessage id="registrations.edit.house-number-addition" />
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="postalCode"
            label={<CustomFormattedMessage id="common.postalcode" />}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="city"
            label={<CustomFormattedMessage id="registrations.edit.city" />}
          />
        </FormRow>

        <h3>
          <CustomFormattedMessage id="registrations.edit.section.care" />
        </h3>

        <FormRow>
          <InputFieldController
            name="pharmacyName"
            label={<CustomFormattedMessage id="registrations.pharmacy" />}
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<Answer>
            type={InputCheckboxType.RADIO}
            name="previousGpInNetherlands"
            label={required('registrations.edit.previous-gp-in-netherlands')}
            options={yesNoOptions()}
          />
        </FormRow>

        {previousGpInNetherlands === 'yes' && (
          <>
            <FormRow>
              <InputFieldController
                name="previousGpName"
                label={required('registrations.edit.previous-gp-name')}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="previousGpCity"
                label={required('registrations.edit.previous-gp-city')}
              />
            </FormRow>
          </>
        )}

        <FormRow>
          <InputTextAreaController
            name="previousGpNote"
            label={
              <CustomFormattedMessage id="registrations.edit.previous-gp-note" />
            }
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<Answer>
            type={InputCheckboxType.RADIO}
            name="recordTransferConsent"
            label={
              previousGpInNetherlands === 'yes' ? (
                required('registrations.record-transfer-consent')
              ) : (
                <CustomFormattedMessage id="registrations.record-transfer-consent" />
              )
            }
            options={yesNoOptions(previousGpInNetherlands !== 'yes')}
          />
        </FormRow>

        {recordTransferConsent === 'no' && (
          <FormRow>
            <InputTextAreaController
              name="recordTransferDeclineReason"
              label={required('registrations.edit.record-transfer-decline')}
            />
          </FormRow>
        )}

        <h3>
          <CustomFormattedMessage id="registrations.insurance" />
        </h3>

        <FormRow>
          <InputCheckboxController<InsuranceType>
            type={InputCheckboxType.RADIO}
            name="insuranceType"
            label={required('registrations.edit.insurance-type')}
            options={[
              {
                key: InsuranceType.DutchBasic,
                title: (
                  <CustomFormattedMessage id="registrations.insurance.dutch-basic" />
                ),
              },
              {
                key: InsuranceType.International,
                title: (
                  <CustomFormattedMessage id="registrations.insurance.international" />
                ),
              },
              {
                key: InsuranceType.Other,
                title: (
                  <CustomFormattedMessage id="registrations.insurance.other" />
                ),
              },
            ]}
          />
        </FormRow>

        <FormRow>
          <InputTextAreaController
            name="insuranceNote"
            label={
              insuranceType === InsuranceType.Other ? (
                required('registrations.edit.insurance-note')
              ) : (
                <CustomFormattedMessage id="registrations.edit.insurance-note" />
              )
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="insuranceCompany"
            label={
              <CustomFormattedMessage id="registrations.edit.insurance-company" />
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="insurancePolicyNumber"
            label={
              <CustomFormattedMessage id="registrations.edit.insurance-policy-number" />
            }
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<Answer>
            type={InputCheckboxType.RADIO}
            name="lspConsent"
            label={required('registrations.lsp-consent')}
            options={yesNoOptions()}
          />
        </FormRow>

        <h3>
          <CustomFormattedMessage id="registrations.contact-person" />
        </h3>

        <FormRow>
          <InputFieldController
            name="contactPersonName"
            label={
              <CustomFormattedMessage id="registrations.edit.contact-person-name" />
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            name="contactPersonRelation"
            label={
              <CustomFormattedMessage id="registrations.edit.contact-person-relation" />
            }
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            type="phone"
            name="contactPersonPhoneNumber"
            label={<CustomFormattedMessage id="common.phone" />}
          />
        </FormRow>

        <FormRow>
          <InputTextAreaController
            name="remarks"
            label={<CustomFormattedMessage id="registrations.remarks" />}
          />
        </FormRow>

        <h3>
          <CustomFormattedMessage id="registrations.edit.section.patient" />
        </h3>
        <p>
          <CustomFormattedMessage id="registrations.edit.section.patient.hint" />
        </p>

        <FormRow>
          <InputCheckboxController<boolean>
            name="mobileApp"
            label={<CustomFormattedMessage id="patient.app-user" />}
            options={[
              {
                key: true,
                title: (
                  <CustomFormattedMessage id="patient.app-user-placeholder" />
                ),
              },
            ]}
          />
        </FormRow>

        <FormRow>
          <InputSelectController<Language>
            name="language"
            label={required('common.language')}
            options={languageData}
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<MfaAuthenticationType>
            type={InputCheckboxType.RADIO}
            name="mfaAuthenticationType"
            label={required('common.mfa-authentication-type')}
            options={[
              {
                key: MfaAuthenticationType.Sms,
                title: (
                  <CustomFormattedMessage id="common.mfa-authentication-type.sms" />
                ),
              },
              {
                key: MfaAuthenticationType.Email,
                title: (
                  <CustomFormattedMessage id="common.mfa-authentication-type.email" />
                ),
              },
            ]}
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<boolean>
            name="dutchInsurance"
            label={<CustomFormattedMessage id="patient.insurance" />}
            options={[
              {
                key: true,
                title: (
                  <CustomFormattedMessage id="patient.insurance-placeholder" />
                ),
              },
            ]}
          />
        </FormRow>

        <FormRow>
          <InputCheckboxController<boolean>
            name="noShowProtocol"
            label={<CustomFormattedMessage id="patient.no-show-protocol" />}
            options={[
              {
                key: true,
                title: (
                  <CustomFormattedMessage id="patient.no-show-protocol-placeholder" />
                ),
              },
            ]}
          />
        </FormRow>
      </FormProvider>
    </Modal>
  );
}
